import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { errMsg, formatBytes, formatDate, useApi } from "@/lib/api";
import ChecksList from "./ChecksList";
import { DeployBadge } from "./Badges";
import { duration, publicUrl, totalKb } from "./helpers";

function Stat({ label, value }) {
  return <div className="rounded-lg border border-slate-200 px-3 py-2"><p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p><p className="mt-0.5 text-sm font-semibold text-slate-900 tabular-nums">{value}</p></div>;
}

function Summary({ d }) {
  const s = d.summary || {}, f = d.files || {}, t = d.timings || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Pagine" value={s.routes != null ? `${s.routes}${s.dynamic ? ` (${s.dynamic} dai dati)` : ""}` : "—"} />
        <Stat label="Sezioni compilate" value={s.sections || "—"} />
        <Stat label="Peso compresso" value={totalKb(s) != null ? `${totalKb(s)} KB` : "—"} />
        <Stat label="Durata" value={duration(d.started_at, d.finished_at)} />
      </div>
      {d.files && <p className="text-xs text-slate-500">{f.count} file ({formatBytes(f.bytes)}) · {f.new_files} nuovi caricati ({formatBytes(f.new_bytes)}), gli altri già presenti per questo sito · preparazione {t.export_ms} ms, compilazione {t.build_ms} ms, salvataggio {t.upload_ms} ms</p>}
      {s.sizes_kb && <p className="text-xs text-slate-500">Compressi: {Object.entries(s.sizes_kb).filter(([, v]) => v).map(([k, v]) => `${k} ${Math.round(v)} KB`).join(" · ")}</p>}
      <p className="text-xs text-slate-500">Contenuto: {d.source?.pages} pagine, {d.source?.sections} sezioni · progetto salvato il {formatDate(d.source?.updated_at, true)} · impronta {d.source?.content_sha?.slice(0, 10)}</p>
      {d.error && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 whitespace-pre-wrap" data-testid="deploy-error">{d.error}</p>}
      {d.url && <a href={publicUrl(d.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="deploy-open-link">Apri questa versione<ExternalLink size={13} /></a>}
    </div>
  );
}

function Routes({ routes }) {
  return (
    <ul className="divide-y divide-slate-100" data-testid="deploy-routes">
      {(routes || []).map((r) => (
        <li key={r.path} className="py-2 text-xs"><code className="text-slate-800">{r.path}</code>{r.dynamic && <span className="ml-2 text-sky-700">dai dati</span>}
          <p className="font-medium text-slate-700">{r.title}</p><p className="text-slate-500 line-clamp-2">{r.description || "— nessuna descrizione —"}</p></li>
      ))}
    </ul>
  );
}

export default function DeployDetailDialog({ siteId, deploy, onOpenChange }) {
  const api = useApi();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); if (deploy) api.get(`/sites/${siteId}/deploys/${deploy.id}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))); }, [api, siteId, deploy]);
  return (
    <Dialog open={!!deploy} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="deploy-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Compilazione n. {deploy?.number} {d && <DeployBadge d={d} />}</DialogTitle>
          <DialogDescription>{d ? `${d.trigger_label} · ${d.requested_by || "automatica"} · ${formatDate(d.created_at, true)}${d.note ? ` · ${d.note}` : ""}` : ""}</DialogDescription>
        </DialogHeader>
        {!d ? <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" />Caricamento…</p> : (
          <Tabs defaultValue="summary">
            <TabsList><TabsTrigger value="summary" data-testid="deploy-tab-summary">Riepilogo</TabsTrigger><TabsTrigger value="checks" data-testid="deploy-tab-checks">Controlli</TabsTrigger><TabsTrigger value="routes" data-testid="deploy-tab-routes">Pagine</TabsTrigger></TabsList>
            <TabsContent value="summary" className="pt-3"><Summary d={d} /></TabsContent>
            <TabsContent value="checks" className="pt-3"><ChecksList checks={d.checks && Object.keys(d.checks).length ? d.checks : null} /></TabsContent>
            <TabsContent value="routes" className="pt-3"><Routes routes={d.report?.routes} /></TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
